// src/utils/checkinHelpers.js
//
// Record-level helpers for the Student CHECK-IN system. This is the check-in
// analogue of checkoutHelpers.js. It deliberately reuses the shared, app-wide
// constants (KA_COLORS, MS/US grade lists, timestamp formatting) from
// checkoutHelpers so the two tools stay visually and behaviorally consistent,
// and pulls the item catalog / audience / permission logic from
// config/checkinItems.js.
//
// Record shape (Firestore: checkinRecords/{schoolYear}_{sisId}):
//   {
//     id, schoolYear, studentId (sisId), studentName, grade, cohort,
//     boarding: bool,            // captured at creation, drives audience
//     isInternational: bool,     // captured at creation, drives audience
//     status: 'in_progress' | 'complete' | 'archived',
//     items: {
//       finance: { status, signedByName, signedByUid, signedAt, notes },
//       ...
//     },
//     createdAt, updatedAt, createdByName,
//   }
//
// "Relevant" items for a record are computed dynamically from the (possibly
// admin-edited) audience config + the student's residency/nationality, mirroring
// how checkout derives relevant sections from the form type. Completion is
// driven by REQUIRED relevant items only — optional items (e.g. Cake Order)
// can be checked off but never block "complete".

import {
  KA_COLORS,
  MS_GRADES,
  US_GRADES,
  formatSignoffTimestamp,
} from './checkoutHelpers';

import {
  CHECKIN_ITEMS,
  DEFAULT_CHECKIN_SCHOOL_YEAR,
  getResolvedCheckinItems,
  itemAppliesToStudent,
  canCheckInItem,
  hasAnyCheckinPermission,
} from '../config/checkinItems';

// Re-export shared + catalog pieces so components can import everything
// check-in related from this one module.
export {
  KA_COLORS,
  MS_GRADES,
  US_GRADES,
  formatSignoffTimestamp,
  CHECKIN_ITEMS,
  getResolvedCheckinItems,
  itemAppliesToStudent,
  canCheckInItem,
  hasAnyCheckinPermission,
};

export const DEFAULT_SCHOOL_YEAR = DEFAULT_CHECKIN_SCHOOL_YEAR;

export const ITEM_STATUS = {
  PENDING: 'pending',
  SIGNED: 'signed', // "cleared" / "received"
  NA: 'na',
};

export const RECORD_STATUS = {
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
  ARCHIVED: 'archived',
};

// ─── IDs ─────────────────────────────────────────────────────────────────

export function buildCheckinRecordId(schoolYear, sisId) {
  return `${schoolYear}_${sisId}`;
}

// ─── Relevance (audience) ───────────────────────────────────────────────────
// A check-in record stores `boarding` / `isInternational` booleans, so it can
// be passed straight into itemAppliesToStudent (whose field reads include
// those exact names).

export function getRelevantCheckinItems(record, resolvedItems) {
  const items = resolvedItems || getResolvedCheckinItems(null);
  if (!record) return [];
  return items.filter((it) => itemAppliesToStudent(it, record));
}

export function getRelevantCheckinItemIds(record, resolvedItems) {
  return getRelevantCheckinItems(record, resolvedItems).map((it) => it.id);
}

export function getItemStatus(record, itemId) {
  return record?.items?.[itemId]?.status || ITEM_STATUS.PENDING;
}

// ─── Completion ─────────────────────────────────────────────────────────────
// Percent is based on REQUIRED relevant items. signed + na both count as done.
// No required relevant items → 100%.

export function computeCheckinCompletion(record, resolvedItems) {
  const relevant = getRelevantCheckinItems(record, resolvedItems);
  const required = relevant.filter((it) => it.required);
  if (required.length === 0) return 100;
  const done = required.filter((it) => {
    const s = getItemStatus(record, it.id);
    return s === ITEM_STATUS.SIGNED || s === ITEM_STATUS.NA;
  }).length;
  return Math.round((done / required.length) * 100);
}

export function isRecordComplete(record, resolvedItems) {
  return computeCheckinCompletion(record, resolvedItems) === 100;
}

// ─── My pending ─────────────────────────────────────────────────────────────
// Relevant items that are still pending AND that this user is authorized to
// check off. Used for the "My Pending" tab and the row highlight.

export function getMyPendingCheckinItems(record, user, resolvedItems) {
  const relevant = getRelevantCheckinItems(record, resolvedItems);
  return relevant.filter(
    (it) =>
      getItemStatus(record, it.id) === ITEM_STATUS.PENDING &&
      canCheckInItem(user, it),
  );
}

// ─── Status presentation ─────────────────────────────────────────────────

export function getStatusLabel(status) {
  switch (status) {
    case ITEM_STATUS.SIGNED:
      return 'Cleared';
    case ITEM_STATUS.NA:
      return 'N/A';
    case ITEM_STATUS.PENDING:
    default:
      return 'Pending';
  }
}

export function getStatusColor(status) {
  switch (status) {
    case ITEM_STATUS.SIGNED:
      return { bg: '#E8F5E9', fg: '#2E7D32', border: '#C8E6C9' };
    case ITEM_STATUS.NA:
      return { bg: '#F5F5F5', fg: '#888', border: '#E0E0E0' };
    case ITEM_STATUS.PENDING:
    default:
      return { bg: '#FFF8E1', fg: '#996515', border: '#FFE082' };
  }
}